import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { ChatPill } from '@atlaskit/rovo-agent-components/common/ui/ChatPill';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onOpenChatButtonClick } from '../../../ai-answer-dialog/analytics';
import { SainAction, useSAINUsageCriteriaMet } from '../../controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../utils/sain-feature-control';
import { ErrorBoundary } from '../error-boundary';

import { messages } from './messages';
type OpenChatButtonProps = {
	prompt: string;
	query: string;
	answerString: string;
	source: 'reading-aids' | 'sain';
	experimentCohort?: string;
};

const styles = cssMap({
	chatButtonContainer: {
		width: 'fit-content',
	},
});

export const OpenChatButton = ({
	prompt,
	answerString,
	query,
	source,
}: OpenChatButtonProps): React.JSX.Element => {
	const { commonAttributes, isReadingAids, onClose, readingAidsOptions } = useAIAnswerContext();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [fireAnalyticsEvent] = useAnalytics();
	const publish = usePublish('ai-mate');
	const { formatMessage } = useIntl();

	const { serveExperiment: serveDefinitionsUXExp } = getFrontendExperiment({
		abTestId: 'kd_definitions_ux_uplift_exp',
	});
	const serveDefinitionUXUpdates = serveDefinitionsUXExp && fg('kd_definitions_ux_uplift_exp_gate');

	const onOpenChatClick = useCallback(() => {
		fireAnalyticsEvent(onOpenChatButtonClick({ ...commonAttributes }));
		fireSAINUsageEvent({ metCriteria: SainAction.OPEN_CHAT_CLICKED, commonAttributes });
		// callback is shared with followups
		// used in the issue model view to open up chat in a new tab
		// this is because we don't have access to dismiss the issue view modal
		if (readingAidsOptions?.onFollowUpCallback) {
			readingAidsOptions.onFollowUpCallback(prompt);
			return;
		}

		publish({
			type: 'chat-new',
			source,
			data: {
				name: query.substring(0, 30),
				dialogues: [
					{
						human_message: {
							content: prompt,
						},
						agent_message: { content: answerString },
					},
				],
				sourceId: commonAttributes.searchSessionId,
			},
		});

		onClose?.();
	}, [
		query,
		answerString,
		publish,
		onClose,
		commonAttributes,
		fireAnalyticsEvent,
		fireSAINUsageEvent,
		prompt,
		source,
		readingAidsOptions,
	]);

	return (
		<ErrorBoundary fallback={<></>}>
			{(serveDefinitionUXUpdates && isReadingAids) || SAINFeatureControl.isBorderlessUXEnabled() ? (
				<Box xcss={styles.chatButtonContainer}>
					<Button
						iconBefore={RovoChatIcon}
						spacing={SAINFeatureControl.isBorderlessUXEnabled() ? 'default' : 'compact'}
						shouldFitContainer={false}
						onClick={onOpenChatClick}
						testId="definition-open-chat-button"
					>
						{formatMessage(messages.ai_answer_component_ai_answer_open_chat)}
					</Button>
				</Box>
			) : (
				<ChatPill onClick={onOpenChatClick}>
					{formatMessage(messages.ai_answer_component_ai_answer_open_chat)}
				</ChatPill>
			)}
		</ErrorBoundary>
	);
};
